import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Clock, TrendingUp, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Auctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions?status=active`);
      setAuctions(response.data);
    } catch (error) {
      console.error('Failed to fetch auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    const now = new Date();
    const end = new Date(endTime);
    const diff = end - now;

    if (diff <= 0) return 'Ended';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-black" data-testid="auctions-page">
      <Navbar />
      
      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="auctions-title">
              Live <span className="accent-red">Auctions</span>
            </h1>
            <p className="text-gray-400 text-lg" data-testid="auctions-description">
              Bid on exclusive collectibles and rare finds. Real-time bidding with timed auctions.
            </p>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold mb-12 text-center" data-testid="auctions-coming-soon">
              Coming <span className="accent-red">Soon....</span>
          </h1>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-4 mb-12">
            <div className="glass-effect p-6 rounded-xl text-center" data-testid="stat-active">
              <TrendingUp className="mx-auto mb-2 text-red-500" size={32} />
              <p className="text-2xl font-bold accent-red">{auctions.length}</p>
              <p className="text-sm text-gray-400">Active Auctions</p>
            </div>
            <div className="glass-effect p-6 rounded-xl text-center" data-testid="stat-bidders">
              <Users className="mx-auto mb-2 text-red-500" size={32} />
              <p className="text-2xl font-bold accent-red">500+</p>
              <p className="text-sm text-gray-400">Active Bidders</p>
            </div>
            <div className="glass-effect p-6 rounded-xl text-center" data-testid="stat-avg-time">
              <Clock className="mx-auto mb-2 text-red-500" size={32} />
              <p className="text-2xl font-bold accent-red">2h 30m</p>
              <p className="text-sm text-gray-400">Avg. Time Left</p>
            </div>
          </div>
          {/* Below Auction grid commented by Danish 13-11-2025 */}
          {/* 
            // Auctions Grid Start ---------------------------------------
            {loading ? (
              <div className="text-center py-20" data-testid="loading">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600 mx-auto mb-4"></div>
                <p className="text-gray-400">Loading auctions...</p>
              </div>
            ) : auctions.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="auctions-grid">
                {auctions.map(auction => (
                  <Link
                    key={auction.id}
                    to={`/auction/${auction.id}`}
                    className="product-card card-hover relative"
                    data-testid={`auction-card-${auction.id}`}
                  >
                    // Live Badge
                    <div className="absolute top-4 left-4 z-10 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1 pulse-red">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      <span>LIVE</span>
                    </div>
                    // Time Remaining
                    <div className="absolute top-4 right-4 z-10 glass-effect px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{getTimeRemaining(auction.end_time)}</span>
                    </div>
                    <div className="relative">
                      <img 
                        src={auction.product?.image_url || 'https://images.unsplash.com/photo-1608278047522-58806a6ac85b'} 
                        alt={auction.product?.name || 'Auction item'} 
                        className="w-full h-64 object-cover" 
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-sm text-red-500 mb-1">{auction.product?.category}</p>
                      <h3 className="text-lg font-semibold mb-3 line-clamp-1">{auction.product?.name}</h3>
                      <div className="flex justify-between items-center mb-2">
                        <div>
                          <p className="text-xs text-gray-400">Current Bid</p>
                          <p className="text-2xl font-bold accent-red">₹{auction.current_bid.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Starting</p>
                          <p className="text-sm text-gray-300">₹{auction.starting_price.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <button className="w-full btn-primary py-2" data-testid={`bid-now-${auction.id}`}>
                          Place Bid
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20" data-testid="no-auctions">
                <p className="text-xl text-gray-400">No active auctions at the moment</p>
                <Link to="/shop" className="btn-primary inline-block mt-6">Browse Shop</Link>
              </div>
            )}
            // Auctions Grid End -----------------------------------------
          */}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Auctions;
